import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import '../css/Bewonersbeheer.css';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';


const Bewonersbeheer = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [bewoners, setBewoners] = useState([]);
  const [oudBewoners, setOudBewoners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'former'
  const [syncing, setSyncing] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [loading, setLoading] = useState({
    current: true,
    former: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    console.log("🧪 Debug bewoners IDs:", bewoners.map(b => ({ naam: b.naam, _id: b._id })));
  }, [bewoners]);
  

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    // Redirect to home page, not login
    navigate('/home');
  };

  // IMPORTANT: Debug log for state changes
  useEffect(() => {
    console.log("Form disabled state changed:", formDisabled);
  }, [formDisabled]);

  // Fetch ALL current residents
  const fetchCurrentBewoners = async () => {
    try {
      setLoading(prev => ({ ...prev, current: true }));
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found, redirecting to login');
        navigate('/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bewoners/get-all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          // Token expired, try to refresh
          await refreshToken();
          return fetchCurrentBewoners();
        }
        throw new Error('Failed to fetch residents');
      }
      
      const data = await response.json();
      
      // Filter out former residents for the main display
      const currentResidents = data.filter(resident => !resident.isFormerResident);
      
      // Add original data for tracking changes
      const dataWithOriginal = currentResidents.map(b => ({
        ...b,
        originalNaam: b.naam,
        originalNummer: b.nummer,
        originalEinddatum: b.einddatum,
        originalIsFormerResident: b.isFormerResident,
        originalInhabited: b.inhabited
      }));
      
      setBewoners(dataWithOriginal);
      const ids = dataWithOriginal.map(b => b._id);
      const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
      console.log("🪵 All IDs:", ids);
      console.log("🚨 Duplicate IDs:", duplicates);
      

      console.log(`Loaded ${dataWithOriginal.length} current residents (filtered out former residents)`);
    } catch (error) {
      console.error('Error fetching residents:', error.message);
      alert('Error fetching residents: ' + error.message);
    } finally {
      setLoading(prev => ({ ...prev, current: false }));
    }
  };

  // Fetch former residents
  const fetchFormerBewoners = async () => {
    try {
      setLoading(prev => ({ ...prev, former: true }));
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found, redirecting to login');
        navigate('/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bewoners/get-oud-bewoners`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          // Token expired, try to refresh
          await refreshToken();
          return fetchFormerBewoners();
        }
        throw new Error('Failed to fetch former residents');
      }
      
      const data = await response.json();
      
      // Add original data for tracking changes
      const dataWithOriginal = data.map(b => ({
        ...b,
        originalNaam: b.naam,
        originalNummer: b.nummer,
        originalEinddatum: b.einddatum,
        originalIsFormerResident: b.isFormerResident,
        originalInhabited: b.inhabited
      }));
      
      setOudBewoners(dataWithOriginal);
      console.log(`Loaded ${dataWithOriginal.length} former residents`);
    } catch (error) {
      console.error('Error fetching former residents:', error.message);
      alert('Error fetching former residents: ' + error.message);
    } finally {
      setLoading(prev => ({ ...prev, former: false }));
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.accessToken);
        return true;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('Error refreshing token:', error.message);
      navigate('/login');
      return false;
    }
  };

  // Initial data load
  useEffect(() => {
    fetchCurrentBewoners();
  }, []);

  // Load former residents when tab changes to 'former'
  useEffect(() => {
    if (activeTab === 'former') {
      fetchFormerBewoners();
    }
  }, [activeTab]);

  // Filter residents based on search term
  const getFilteredResidents = (residents) => {
    return residents.filter(bewoner => {
      const term = searchTerm.toLowerCase();
      return (
        (bewoner.naam && bewoner.naam.toLowerCase().includes(term)) ||
        (bewoner.nummer && String(bewoner.nummer).toLowerCase().includes(term)) ||
        (bewoner.email && bewoner.email.toLowerCase().includes(term))
      );
    });
  };

  const filteredBewoners = getFilteredResidents(bewoners);
  const filteredOudBewoners = getFilteredResidents(oudBewoners);

  // EXTREMELY SIMPLIFIED input change handler for debugging
  const handleCurrentBewonerInputChange = (e, index, field) => {
    const newBewoners = [...bewoners];
    newBewoners[index] = {
      ...newBewoners[index],
      [field]: e.target.value
    };
    setBewoners(newBewoners);
  };

  // Input change handler for former residents
  const handleFormerBewonerInputChange = (e, index, field) => {
    console.log(`Former bewoner input change: ${field} = ${e.target.value}`);
    
    const newOudBewoners = [...oudBewoners];
    newOudBewoners[index] = {
      ...newOudBewoners[index],
      [field]: e.target.value
    };
    setOudBewoners(newOudBewoners);
  };
  // Save function
  const handleSave = async (bewoner, index, isFormer = false) => {
    try {
      console.log("Disabling form for save operation");
      setFormDisabled(true);
      
      const token = localStorage.getItem('token');
      
      // Check if an end date was added or removed
      const hasEndDate = bewoner.einddatum && bewoner.einddatum.trim() !== '';
      
      // Make copy to avoid mutating original state directly
      const bewoner_copy = { ...bewoner };
      
      // EXPLICITLY set Boolean values (not empty strings)
      bewoner_copy.isFormerResident = hasEndDate ? true : false;
      bewoner_copy.inhabited = hasEndDate ? false : true;
      
      console.log(`Saving resident ${bewoner_copy.naam} (${bewoner_copy._id})`);
      console.log(`Former resident: ${bewoner_copy.isFormerResident}, Inhabited: ${bewoner_copy.inhabited}`);
      console.log(`End date: ${bewoner_copy.einddatum || 'None'}`);
      
      // Use the correct endpoint based on resident type
      const endpoint = isFormer ? 
        `${process.env.REACT_APP_API_URL}/api/bewoners/update-former-resident/${bewoner._id}` :
        `${process.env.REACT_APP_API_URL}/api/bewoners/update/${bewoner._id}`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bewoner_copy)
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          // Token expired, try to refresh
          await refreshToken();
          return handleSave(bewoner, index, isFormer);
        }
        throw new Error('Failed to update resident');
      }
      
      const result = await response.json();
      
      console.log('Update response:', result);
      
      // Update the resident data in state
      if (isFormer) {
        const updated = [...oudBewoners];
        updated[index] = {
          ...result.resident,
          originalNaam: result.resident.naam,
          originalNummer: result.resident.nummer,
          originalEinddatum: result.resident.einddatum,
          originalIsFormerResident: result.resident.isFormerResident,
          originalInhabited: result.resident.inhabited
        };
        setOudBewoners(updated);
        
        // If the resident is no longer a former resident (einddatum removed),
        // move them to current residents and refresh both lists
        if (!result.resident.isFormerResident) {
          alert(`✅ ${result.resident.naam} is now a current resident`);
          fetchCurrentBewoners();
          fetchFormerBewoners();
        }
      } else {
        const updated = [...bewoners];
        updated[index] = {
          ...result.resident,
          originalNaam: result.resident.naam,
          originalNummer: result.resident.nummer,
          originalEinddatum: result.resident.einddatum,
          originalIsFormerResident: result.resident.isFormerResident,
          originalInhabited: result.resident.inhabited
        };
        setBewoners(updated);
        
        // If the resident is now a former resident (einddatum added),
        // move them to former residents and refresh both lists
        if (result.resident.isFormerResident) {
          alert(`✅ ${result.resident.naam} is now a former resident`);
          fetchCurrentBewoners();
          if (activeTab === 'former') {
            fetchFormerBewoners();
          }
        }
      }
      
      alert(`✅ ${result.resident.naam} updated successfully!`);
      
    } catch (error) {
      console.error('Error updating resident:', error.message);
      alert(`❌ Error: ${error.message}`);
    } finally {
      console.log("Re-enabling form after save operation");
      setFormDisabled(false);
    }
  };

  const handleDeduplicate = async () => {
    if (window.confirm('⚠️ Dit zal duplicaten samenvoegen. Wil je doorgaan?')) {
      try {
        setSyncing(true);
        setFormDisabled(true);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bewoners/deduplicate`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(`✅ Duplicaten opgeschoond!\nSamenvoegingen: ${result.merged}\nVerwijderd: ${result.removed}`);
          
          // Refresh both lists
          fetchCurrentBewoners();
          if (activeTab === 'former') {
            fetchFormerBewoners();
          }
        } else {
          if (response.status === 403) {
            await refreshToken();
            handleDeduplicate();
            return;
          }
          alert('❌ Fout bij opschonen van duplicaten!');
        }
      } catch (error) {
        console.error('Error deduplicating:', error.message);
        alert('❌ Fout bij opschonen van duplicaten!');
      } finally {
        setSyncing(false);
        setFormDisabled(false);
      }
    }
  };

  // SIMPLIFIED sync functions (the rest of the functionality remains the same)
  const handleSyncFromIsta= async () => {
    if (window.confirm('⚡ Haal ingangsdatum, einddatum en inhabited status op van ISTA?⚠️ Let op! Alleen gebruiken als je zeker weet dat Ista data up-to-date is. Anders importeert hij niet bekende bewoners als nieuwe bewoners - als de verhuizing nog niet is doorgevoerd in Ista.\n\nWil je doorgaan?')) {
      try {
        setSyncing(true);
        setFormDisabled(true);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bewoners/sync-from-ista`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(`✅ ISTA synchronisatie geslaagd!\nBijgewerkt: ${result.updatedCount}\nNiet gevonden: ${result.notFoundCount}\nOud-bewoners gemarkeerd: ${result.formerResidentsCount}`);
          
          // Refresh both lists
          fetchCurrentBewoners();
          if (activeTab === 'former') {
            fetchFormerBewoners();
          }
        } else {
          if (response.status === 403) {
            await refreshToken();
            handleSyncFromIsta();
            return;
          }
          alert('❌ Fout bij synchroniseren met ISTA!');
        }
      } catch (error) {
        console.error('Error syncing from ISTA:', error.message);
        alert('❌ Fout bij synchroniseren met ISTA!');
      } finally {
        setSyncing(false);
        setFormDisabled(false);
      }
    }
  };
  
  const handleSyncFormerResidents = async () => {
    if (window.confirm('⚡ Sync alle oud-bewoners vanuit Google Sheet?')) {
      try {
        setSyncing(true);
        setFormDisabled(true);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bewoners/sync-former-residents`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(`✅ Oud-bewoners succesvol gesynchroniseerd!\nBijgewerkt: ${result.updatedCount}\nToegevoegd: ${result.insertedCount}\nTotaal: ${result.totalCount}`);
          
          // Refresh both lists
          if (activeTab === 'former') {
            fetchFormerBewoners();
          }
          fetchCurrentBewoners();
        } else {
          if (response.status === 403) {
            await refreshToken();
            handleSyncFormerResidents();
            return;
          }
          alert('❌ Fout bij synchroniseren van oud-bewoners!');
        }
      } catch (error) {
        console.error('Error syncing former residents:', error.message);
        alert('❌ Fout bij synchroniseren van oud-bewoners!');
      } finally {
        setSyncing(false);
        setFormDisabled(false);
      }
    }
  };
  
  const handleSyncFromSheet = async () => {
    if (window.confirm('⚡ Sync alle bewoners opnieuw vanuit Google Sheet?')) {
      try {
        setSyncing(true);
        setFormDisabled(true);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bewoners/sync-from-sheet`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(`✅ Bewoners succesvol gesynchroniseerd!\nAantal bewoners: ${result.bewonersCount}\nOud-bewoners behouden: ${result.formerResidentsPreserved || 0}`);
          
          // Refresh both lists
          fetchCurrentBewoners();
          if (activeTab === 'former') {
            fetchFormerBewoners();
          }
        } else {
          if (response.status === 403) {
            await refreshToken();
            handleSyncFromSheet();
            return;
          }
          alert('❌ Fout bij synchroniseren!');
        }
      } catch (error) {
        console.error('Error syncing:', error.message);
        alert('❌ Fout bij synchroniseren!');
      } finally {
        setSyncing(false);
        setFormDisabled(false);
      }
    }
  };
  
  const handleFixResidentStatus = async () => {
    if (window.confirm('⚡ Bewoners status repareren? Dit zorgt ervoor dat alle bewoners met een einddatum correct als oud-bewoners worden gemarkeerd.')) {
      try {
        setSyncing(true);
        setFormDisabled(true);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bewoners/fix-resident-status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(`✅ Bewoners status gerepareerd!\nAangepast: ${result.fixedCount}`);
          
          // Refresh both lists
          fetchCurrentBewoners();
          if (activeTab === 'former') {
            fetchFormerBewoners();
          }
        } else {
          if (response.status === 403) {
            await refreshToken();
            handleFixResidentStatus();
            return;
          }
          alert('❌ Fout bij repareren van bewoners status!');
        }
      } catch (error) {
        console.error('Error fixing resident status:', error.message);
        alert('❌ Fout bij repareren van bewoners status!');
      } finally {
        setSyncing(false);
        setFormDisabled(false);
      }
    }
  };

  // Function to handle external links
  const openExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Debug logging
  console.log("Form disabled state:", formDisabled);
  console.log("Loading state:", loading);

  // SIMPLIFIED resident form with direct input handlers
  const renderResidentForm = (bewoner, index, isFormer = false) => {
    console.log(`Rendering form for ${bewoner.naam}, disabled: ${formDisabled}`);
    
    return (
      <div className="card" key={bewoner._id}>

        {isFormer && <div className="former-resident-badge">Oud-bewoner</div>}
        <div className="card-grid">
          
          {/* Left column */}
          <div className="column">
            {[
              { label: 'Nummer', field: 'nummer' },
              { label: 'Postcode', field: 'postcode' },
              { label: 'Etage', field: 'etage' },
              { label: 'Zijde', field: 'zijde' },
              { label: 'Category', field: 'category' },
              { label: 'Ingangsdatum', field: 'ingangsdatum' },
              { label: 'Einddatum', field: 'einddatum' }
            ].map(({ label, field }) => (
              <div className="form-group" key={field}>
                <label>{label}</label>
                <input
                  type="text"
                  value={bewoner[field] !== undefined && bewoner[field] !== null ? String(bewoner[field]) : ''}

                  onChange={(e) => {
                    if (isFormer) {
                      handleFormerBewonerInputChange(e, index, field);
                    } else {
                      handleCurrentBewonerInputChange(e, index, field);
                    }
                  }}
                  disabled={formDisabled}
                  className={field === 'einddatum' ? 'end-date-field' : ''}
                />
              </div>
            ))}
          </div>

          {/* Right column */}
          <div className="column">
            {[
              { label: 'Naam', field: 'naam' },
              { label: 'Aanspreektitel', field: 'aanspreektitel' },
              { label: 'Email', field: 'email' },
              { label: 'Email 2', field: 'email2' },
              { label: 'Telefoon', field: 'telefoon' },
              { label: 'Telefoon 2', field: 'telefoon2' },
              { label: 'Rol', field: 'rol' }
            ].map(({ label, field }) => (
              <div className="form-group" key={field}>
                <label>{label}</label>
                <input
  type="text"
  value={bewoner[field] !== undefined && bewoner[field] !== null ? String(bewoner[field]) : ''}
  onChange={(e) => {
    if (isFormer) {
      handleFormerBewonerInputChange(e, index, field);
    } else {
      handleCurrentBewonerInputChange(e, index, field);
    }
  }}
  disabled={formDisabled}
  className={field === 'einddatum' ? 'end-date-field' : ''}
/>

              </div>
            ))}
          </div>
        </div>

        {/* Save button */}
        <div style={{ marginTop: '10px', textAlign: 'right' }}>
          <button 
            onClick={() => handleSave(bewoner, index, isFormer)}
            className="save-button"
            disabled={formDisabled}
          >
            {formDisabled ? '⏳ Bezig...' : '💾 Save'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="beheer-wrapper">
  <BeheerdersportaalSidebar>
      <main className="dashboard">
        <h1>👥 Bewonersbeheer</h1>

        {/* Sync buttons */}
        <div className="sync-buttons">
          <button 
            onClick={handleSyncFromSheet} 
            className="sync-button"
            disabled={syncing || formDisabled}
          >
            {syncing ? '⏳ Bezig...' : '🔄 Sync from Google Sheet'}
          </button>
          
        

          <button 
            onClick={handleSyncFormerResidents}
            className="sync-button former-residents-button"
            disabled={syncing || formDisabled}
          >
            {syncing ? '⏳ Bezig...' : '🔄 Sync Oud Bewoners Sheet'}
          </button>

          <button 
            onClick={handleFixResidentStatus}
            className="sync-button fix-button"
            disabled={syncing || formDisabled}
          >
            {syncing ? '⏳ Bezig...' : '🔧 Fix Bewoners Status'}
          </button>

          <button 
  onClick={handleDeduplicate}
  className="sync-button"
  disabled={syncing || formDisabled}
>
  {syncing ? '⏳ Bezig...' : '🧹 Duplicaten Opschonen'}
</button>

<button 
  onClick={handleSyncFromIsta}
  className="sync-button ista-button danger-button" // Add danger-button class
  disabled={syncing || formDisabled}
>
  {syncing ? '⏳ Bezig...' : '📅 Sync Dates from ISTA'}
</button>
        </div>

        {/* Tab navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'current' ? 'active' : ''}`}
            onClick={() => setActiveTab('current')}
          >
            👥 Huidige Bewoners
          </button>
          <button 
            className={`tab-button ${activeTab === 'former' ? 'active' : ''}`}
            onClick={() => setActiveTab('former')}
          >
            👤 Oud Bewoners
          </button>
        </div>

        {/* Search bar */}
        <input 
          type="text" 
          placeholder="🔍 Zoek bewoners op naam, nummer, email..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          disabled={formDisabled}
        />

        {/* Current Residents Tab */}
        {activeTab === 'current' && (
          <div className="residents-section">
            <h2>Huidige Bewoners</h2>
            <p className="info-text">
              Huidige bewoners van Polderzicht. Om een bewoner als oud-bewoner te markeren, 
              voeg een einddatum toe en klik op Save.
            </p>

            {loading.current ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Gegevens laden...</p>
              </div>
            ) : (
              <Masonry 
                breakpointCols={{ default: 2, 900: 1 }} 
                className="my-masonry-grid" 
                columnClassName="my-masonry-grid_column"
              >
                {filteredBewoners.length > 0 ? (
                  filteredBewoners
                  .filter(b => b._id)
                  .map(filteredBewoner => {
                    const indexInOriginal = bewoners.findIndex(b => b._id === filteredBewoner._id);
                    return renderResidentForm(filteredBewoner, indexInOriginal, false);
                  })
                
                ) : (
                  <div className="no-results">
                    <p>Geen bewoners gevonden die voldoen aan de zoekcriteria.</p>
                  </div>
                )}
              </Masonry>
            )}
          </div>
        )}

        {/* Former Residents Tab */}
        {activeTab === 'former' && (
          <div className="residents-section">
            <h2>Oud Bewoners</h2>
            <p className="info-text">
              Bewoners die niet meer in Polderzicht wonen. Om een oud-bewoner weer actief te maken,
              verwijder de einddatum en klik op Save.
            </p>

            {loading.former ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Gegevens laden...</p>
              </div>
            ) : (
              <Masonry 
                breakpointCols={{ default: 2, 900: 1 }} 
                className="my-masonry-grid" 
                columnClassName="my-masonry-grid_column"
              >
                {filteredOudBewoners.length > 0 ? (
                  filteredOudBewoners.map((bewoner, index) => 
                    renderResidentForm(bewoner, index, true)
                  )
                ) : (
                  <div className="no-results">
                    <p>Geen oud-bewoners gevonden die voldoen aan de zoekcriteria.</p>
                    <button 
                      onClick={fetchFormerBewoners} 
                      className="refresh-button"
                      disabled={loading.former}
                    >
                      {loading.former ? '⏳ Bezig...' : '🔄 Ververs oud-bewoners lijst'}
                    </button>
                  </div>
                )}
              </Masonry>
            )}
          </div>
        )}
      </main>
      </BeheerdersportaalSidebar>
    </div>
  );
};

export default Bewonersbeheer;